// src/utils/dateHelpers.ts - Helpers pour la gestion des dates

import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import i18n from '../i18n';

dayjs.extend(isoWeek);

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const startOfUtcDay = (d: Date): Date =>
  new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0));

const endOfUtcDay = (d: Date): Date =>
  new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 59, 59, 999));

const addUtcDays = (d: Date, days: number): Date => new Date(d.getTime() + days * MS_PER_DAY);

/**
 * En français, les abréviations de mois incluent souvent un point (ex: "oct.").
 * Pour l'affichage compact dans l'app, on supprime ce point.
 */
const stripTrailingDot = (s: string): string => s.replace(/\./g, '');

/**
 * Obtient le début de semaine (lundi) pour une date donnée
 * Utilise dayjs avec le plugin isoWeek pour une gestion correcte
 * 
 * @param date - La date pour laquelle calculer le début de semaine
 * @returns Date du lundi de la semaine (00:00:00 UTC)
 */
export const getWeekStart = (date: Date): Date => {
  const utcDayStart = startOfUtcDay(date);
  // JS: 0=dimanche..6=samedi → ISO start lundi.
  const utcDow = utcDayStart.getUTCDay();
  const daysSinceMonday = (utcDow + 6) % 7; // lundi=0, dimanche=6
  return addUtcDays(utcDayStart, -daysSinceMonday);
};

/**
 * Génère les 7 jours d'une semaine (du lundi au dimanche)
 * 
 * @param weekStart - Date du lundi de la semaine
 * @returns Array de 7 dates (lundi à dimanche)
 */
export const generateWeekDays = (weekStart: Date): Date[] => {
  const mondayUtc = getWeekStart(weekStart);
  return Array.from({ length: 7 }, (_, index) => {
    return addUtcDays(mondayUtc, index);
  });
};

/**
 * Obtient la fin de semaine (dimanche 23:59:59) pour une date donnée
 * 
 * @param weekStart - Date du lundi de la semaine
 * @returns Date du dimanche de la semaine (23:59:59 UTC)
 */
export const getWeekEnd = (weekStart: Date): Date => {
  const mondayUtc = getWeekStart(weekStart);
  return endOfUtcDay(addUtcDays(mondayUtc, 6));
};

/**
 * Vérifie si une date est dans une semaine donnée
 * 
 * @param date - La date à vérifier
 * @param weekStart - Date du lundi de la semaine
 * @returns true si la date est dans la semaine
 */
export const isDateInWeek = (date: Date, weekStart: Date): boolean => {
  const weekEnd = getWeekEnd(weekStart);
  return date >= weekStart && date <= weekEnd;
};

/**
 * Formate une date pour l'affichage dans l'interface
 * 
 * @param date - La date à formater
 * @param format - Le format de date (par défaut: 'dddd DD MMMM')
 * @returns Date formatée
 */
export const formatDateForDisplay = (date: Date, format: string = 'dddd DD MMMM'): string => {
  return dayjs(date).format(format);
};

/**
 * Formate une plage de dates pour l'affichage
 * 
 * @param weekStart - Date du lundi de la semaine
 * @returns String formatée "Semaine du DD MMM - DD MMM YYYY"
 */
export const formatWeekRange = (weekStart: Date): string => {
  const weekEnd = getWeekEnd(weekStart);
  const localeTag = i18n.language === 'en' ? 'en-GB' : 'fr-FR';
  const fmtStart = new Intl.DateTimeFormat(localeTag, {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
  });
  const fmtEnd = new Intl.DateTimeFormat(localeTag, {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  const startFormatted = stripTrailingDot(fmtStart.format(getWeekStart(weekStart)));
  const endFormatted = stripTrailingDot(fmtEnd.format(weekEnd));
  return String(
    i18n.t('appointments.weekRange', {
      ns: 'activity',
      start: startFormatted,
      end: endFormatted,
    })
  );
};

/**
 * Navigue vers la semaine précédente
 * 
 * @param currentWeekStart - Date du lundi de la semaine courante
 * @returns Date du lundi de la semaine précédente
 */
export const goToPreviousWeek = (currentWeekStart: Date): Date => {
  const mondayUtc = getWeekStart(currentWeekStart);
  return addUtcDays(mondayUtc, -7);
};

/**
 * Navigue vers la semaine suivante
 * 
 * @param currentWeekStart - Date du lundi de la semaine courante
 * @returns Date du lundi de la semaine suivante
 */
export const goToNextWeek = (currentWeekStart: Date): Date => {
  const mondayUtc = getWeekStart(currentWeekStart);
  return addUtcDays(mondayUtc, 7);
};

/**
 * Obtient la semaine courante (lundi de cette semaine)
 * 
 * @returns Date du lundi de la semaine courante
 */
export const getCurrentWeekStart = (): Date => {
  return getWeekStart(new Date());
};

/**
 * Index du jour (0 = lundi) vers lequel scroller dans le planning hebdo.
 * - Semaine courante → aujourd’hui
 * - Autre semaine avec RDV → jour dont la date est la plus proche d’aujourd’hui
 * - Semaine vide → premier jour (lundi)
 */
export const resolveWeekScrollDayIndex = (input: {
  weekDays: Date[];
  dayHasEvents: boolean[];
  today?: Date;
}): number => {
  const { weekDays, dayHasEvents } = input;
  if (!weekDays.length) return 0;

  const today = new Date(input.today || new Date());
  today.setHours(0, 0, 0, 0);

  const todayIndex = weekDays.findIndex((d) => {
    const day = new Date(d);
    day.setHours(0, 0, 0, 0);
    return day.getTime() === today.getTime();
  });
  if (todayIndex !== -1) return todayIndex;

  let bestIndex = -1;
  let bestDist = Number.POSITIVE_INFINITY;
  weekDays.forEach((d, index) => {
    if (!dayHasEvents[index]) return;
    const day = new Date(d);
    day.setHours(0, 0, 0, 0);
    const dist = Math.abs(day.getTime() - today.getTime());
    if (dist < bestDist) {
      bestDist = dist;
      bestIndex = index;
    }
  });

  return bestIndex === -1 ? 0 : bestIndex;
};

/** Mode de validation des rapports d'activité (jour / semaine / mois) */
export type ActivityValidationMode = 'daily' | 'weekly' | 'monthly';

/** Jour de fin de délai hebdomadaire (semaine ISO : lundi → dimanche) */
export type WeeklyDeadlineDay =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

export const DEFAULT_ACTIVITY_REPORT_DEADLINE_HOUR = 23;

/** Offset depuis le lundi ISO (0 = lundi … 6 = dimanche) */
export const WEEKLY_DEADLINE_DAY_ISO_OFFSET: Record<WeeklyDeadlineDay, number> = {
  monday: 0,
  tuesday: 1,
  wednesday: 2,
  thursday: 3,
  friday: 4,
  saturday: 5,
  sunday: 6,
};

export interface ActivityReportDeadlineOptions {
  weeklyDeadlineDay?: WeeklyDeadlineDay;
  /** Heure max de validation (0–23). Défaut 23 (= comportement fin de journée). */
  deadlineHour?: number;
}

export const clampDeadlineHour = (hour: number | null | undefined): number => {
  if (hour == null || !Number.isFinite(hour)) return DEFAULT_ACTIVITY_REPORT_DEADLINE_HOUR;
  return Math.min(23, Math.max(0, Math.floor(Number(hour))));
};

const applyDeadlineHour = (base: dayjs.Dayjs, deadlineHour: number): Date =>
  base.hour(clampDeadlineHour(deadlineHour)).minute(0).second(0).millisecond(0).toDate();

/**
 * Calcule la deadline de validation pour un rapport.
 * - daily : jour du rapport à `deadlineHour`
 * - weekly : jour choisi de la semaine ISO à `deadlineHour`
 * - monthly : dernier jour du mois à `deadlineHour`
 */
export const getActivityReportValidationDeadline = (
  reportDate: Date,
  validationMode: ActivityValidationMode,
  options: ActivityReportDeadlineOptions | WeeklyDeadlineDay = {}
): Date => {
  const opts: ActivityReportDeadlineOptions =
    typeof options === 'string' ? { weeklyDeadlineDay: options } : options ?? {};
  const weeklyDeadlineDay = opts.weeklyDeadlineDay ?? 'sunday';
  const deadlineHour = clampDeadlineHour(opts.deadlineHour);
  const d = dayjs(reportDate);

  if (validationMode === 'daily') {
    return applyDeadlineHour(d.startOf('day'), deadlineHour);
  }

  if (validationMode === 'weekly') {
    const offset = WEEKLY_DEADLINE_DAY_ISO_OFFSET[weeklyDeadlineDay] ?? 6;
    return applyDeadlineHour(d.startOf('isoWeek').add(offset, 'day'), deadlineHour);
  }

  return applyDeadlineHour(d.endOf('month').startOf('day'), deadlineHour);
};

/**
 * Indique si un rapport a été validé en retard (validatedAt > deadline).
 */
export const isActivityReportValidatedLate = (
  validatedAt: Date | undefined,
  reportDate: Date,
  validationMode: ActivityValidationMode,
  options: ActivityReportDeadlineOptions | WeeklyDeadlineDay = {}
): boolean => {
  if (!validatedAt) return false;
  const deadline = getActivityReportValidationDeadline(reportDate, validationMode, options);
  return new Date(validatedAt).getTime() > deadline.getTime();
};

/**
 * Nombre de jours de retard (validatedAt après la deadline). 0 si pas en retard.
 */
export const getActivityReportDelayDays = (
  validatedAt: Date | undefined,
  reportDate: Date,
  validationMode: ActivityValidationMode,
  options: ActivityReportDeadlineOptions | WeeklyDeadlineDay = {}
): number => {
  if (!validatedAt) return 0;
  const deadline = getActivityReportValidationDeadline(reportDate, validationMode, options);
  const deadlineMs = deadline.getTime();
  const validatedMs = new Date(validatedAt).getTime();
  if (validatedMs <= deadlineMs) return 0;
  return Math.ceil((validatedMs - deadlineMs) / (24 * 60 * 60 * 1000));
};

/**
 * Calcule le début de période (jour / semaine / mois) pour une date donnée
 * en fonction du mode de validation des rapports d'activité.
 */
export const getPeriodStartForValidationMode = (
  date: Date,
  validationMode: ActivityValidationMode
): Date => {
  const d = dayjs(date);
  if (validationMode === 'daily') {
    return d.startOf('day').toDate();
  }
  if (validationMode === 'weekly') {
    // On utilise isoWeek pour garantir un lundi comme début de semaine
    return d.startOf('isoWeek').toDate();
  }
  return d.startOf('month').toDate();
};

